import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  Grid,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import DynamicTable from "../../../../components/lab-dynamic-table";
import { ArrowBack, Restore } from "@mui/icons-material";

// --- TYPE DEFINITIONS ---
interface DeletedIncomeRecord {
  _id: string;
  incomeCategory: string;
  date: string;
  amount: number;
  incomeSourseType: string;
  description: string;
  payment_mode: string;
  patientId: string;
  userId: string;
  projectId: string;
  attachmentUrl?: string;
  attachmentPath?: string;
  receiptNo: number;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  isActive: boolean;
  deletedAt: string;
  deleteReason?: string;
  patientInfo: {
    patientName: string;
    mrn: number;
    guardiansName: string;
    phonNumber: string;
    gender: string;
  };
  createdBy: {
    _id: string;
    fullName: string;
  };
  deletedBy?: {
    _id: string;
    fullName: string;
  };
}

// --- HELPER FUNCTION ---
const formatIndianNumber = (num: number): string => {
  if (isNaN(num)) return "0";
  const numStr = Math.floor(num).toString();
  const len = numStr.length;
  if (len <= 3) return numStr;
  let result = "";
  let count = 0;
  for (let i = len - 1; i >= 0; i--) {
    result = numStr[i] + result;
    if (count % 2 === 1 && count < 5 && i > 0) {
      result = "," + result;
    }
    count++;
  }
  return result;
};

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// --- MAIN COMPONENT ---
const DeletedIncomeHistory: React.FC = () => {
  const nowDate = new Date().toISOString().split("T")[0];

  // --- STATE MANAGEMENT ---
  const [deletedIncomes, setDeletedIncomes] = useState<DeletedIncomeRecord[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<string>("Today");
  const [fromDate, setFromDate] = useState<string>(nowDate);
  const [toDate, setToDate] = useState<string>(nowDate);

  // --- REDUX & AUTH ---
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const token = localStorage.getItem("token");

  // --- API DATA FETCHING ---
  const fetchDeletedIncomes = useCallback(async (): Promise<void> => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_ADMIN_API_URL}/income/getDeletedIncomeHistory`;
      const params = new URLSearchParams({ fromDate, toDate });
      const response = await axios.get(`${url}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.isSuccess) {
        const records = response.data.data || [];
        setDeletedIncomes(records);
      } else {
        toast.error(response.data.message || "Failed to fetch deleted records");
        setDeletedIncomes([]);
      }
    } catch (err: any) {
      console.error("Fetch Deleted Incomes Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while fetching records"
      );
      setDeletedIncomes([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, token]);

  // --- EFFECT TO FETCH ON DATE CHANGE ---
  useEffect(() => {
    fetchDeletedIncomes();
  }, [fetchDeletedIncomes]);

  // --- HANDLER FOR DATE RANGE DROPDOWN ---
  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setDateRange(value);
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    switch (value) {
      case "Today":
        setFromDate(formatDate(today));
        setToDate(formatDate(today));
        break;
      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        setFromDate(formatDate(yesterday));
        setToDate(formatDate(today));
        break;
      case "Last 7 days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setFromDate(formatDate(sevenDaysAgo));
        setToDate(formatDate(today));
        break;
      case "Last 1 month":
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        setFromDate(formatDate(oneMonthAgo));
        setToDate(formatDate(today));
        break;
      case "Last 6 months":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        setFromDate(formatDate(sixMonthsAgo));
        setToDate(formatDate(today));
        break;
      case "Last 1 year":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        setFromDate(formatDate(oneYearAgo));
        setToDate(formatDate(today));
        break;
      case "custom":
        break;
    }
  };

  // --- RESTORE INCOME HANDLER ---
  const handleRestoreIncome = async (rowIndex: number): Promise<void> => {
    const record = deletedIncomes?.[rowIndex];
    if (!record?._id) {
      toast.error("Invalid record selected");
      return;
    }

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_ADMIN_API_URL}/income/restoreDeletedIncome/${record._id}`;
      const response = await axios.put(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { isSuccess, message } = response.data;
      if (isSuccess) {
        toast.success(message || "Income restored successfully");
        await fetchDeletedIncomes(); // Refresh after restore
      } else {
        toast.error(message || "Failed to restore income");
      }
    } catch (err: any) {
      console.error("Restore Income Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while restoring income"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
      date.getDate()
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // --- RENDER METHOD ---
  return (
    <Box
      sx={{
        background: "#fff",
        p: 2,
        borderRadius: "12px",
        maxHeight: "90vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#077DD3" },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(7, 125, 211, 0.24)",
        },
      }}
    >
      {/* --- COMBINED HEADER AND FILTERING SECTION --- */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: "#fff",
          borderRadius: "12px",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#000" }}>
          <span>
            <ArrowBack
              onClick={() => window.history.back()}
              sx={{ cursor: "pointer", color: "#077DD3" }}
            />
          </span>{" "}
          Income Delete History
        </Typography>

        <Box
          display="flex"
          gap={1.5}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Date Limit
            </Typography>
            <Select
              size="small"
              value={dateRange}
              onChange={handleDateRangeChange}
              className="custom-input"
              sx={{ minWidth: "120px" }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="Yesterday">Yesterday</MenuItem>
              <MenuItem value="Last 7 days">Last 7 days</MenuItem>
              <MenuItem value="Last 1 month">Last 1 month</MenuItem>
              <MenuItem value="Last 6 months">Last 6 months</MenuItem>
              <MenuItem value="Last 1 year">Last 1 year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              From Date
            </Typography>
            <TextField
              className="custom-input"
              type="date"
              size="small"
              value={fromDate}
              onChange={e => {
                setFromDate(e.target.value);
                setDateRange("custom");
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              To Date
            </Typography>
            <TextField
              className="custom-input"
              type="date"
              size="small"
              value={toDate}
              onChange={e => {
                setToDate(e.target.value);
                setDateRange("custom");
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "50vh" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            headers={[
              "SN",
              "MRN",
              "Pt Name",
              "Description",
              "Date",
              "Category",
              "Source Type",
              "Amount",
              "Mode",
              "Attachment",
              "Deleted By",
              "Deleted At",
              "Action",
            ]}
            data={deletedIncomes.map((record, index) => [
              index + 1,
              record.patientInfo?.mrn || "N/A",
              record.patientInfo?.patientName || "N/A",
              record.description || "N/A",
              record.date ? formatDate(new Date(record.date)) : "N/A",
              record.incomeCategory || "N/A",
              record.incomeSourseType || "N/A",
              `PKR ${formatIndianNumber(
                parseFloat(record.amount.toString())
              )}.${
                parseFloat(record.amount.toString()).toFixed(2).split(".")[1] ||
                "00"
              }`,
              record.payment_mode || "N/A",
              record.attachmentUrl ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={`${record.attachmentUrl}`}
                    alt="Attachment"
                    style={{
                      width: "40px",
                      height: "35px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                    onError={e => {
                      e.currentTarget.style.display = "none";
                      const nextSibling = e.currentTarget
                        .nextSibling as HTMLElement;
                      if (nextSibling) nextSibling.style.display = "block";
                    }}
                  />
                </Box>
              ) : (
                "No"
              ),
              record.deletedBy || "N/A",
              record.deletedAt
                ? formatDateTime(new Date(record.deletedAt))
                : "N/A",
            ])}
            height={"60vh"}
            actionButtons={[
              {
                icon: (rowIndex: number) => {
                  return (
                    <Tooltip title="Restore income" arrow placement="top">
                      <Restore sx={{ color: "green", cursor: "pointer" }} />
                    </Tooltip>
                  );
                },
                onClick: (rowIndex: number) => {
                  handleRestoreIncome(rowIndex);
                },
              },
            ]}
          />
        )}
      </Box>
    </Box>
  );
};

export default DeletedIncomeHistory;
